<script setup lang="ts">
import { __, n__ } from '@@/bits/intl'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import OzBadge, { OzBadgeColorScheme, OzBadgeSizePreset } from '@@/library/v4/components/OzBadge.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzListRow, { OzListRowSeparator } from '@@/library/v4/components/OzListRow.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import OzRelativeTime from '@@/library/v4/components/OzRelativeTime.vue'
// TODO: porbably best practice to move this type out of the pinia store to a types file, for now disabling the ww.orizuru_should_not_depend_on_stores rule
import type { WallWithCurrentUserInfo } from '@@/pinia/padlet_picker' // goodcheck-disable-line
import type { Library, WallCamelCase as Wall, Wall as WallSnakeCase } from '@@/types'
import { computed } from 'vue'

const {
  darkMode = 'auto',
  invertColors = false,
  /**
   * The timestamp type to show.
   */
  dateAttribute = 'updatedAt',
  clickable = true,
  navigateOnClick = true,
  xMore = false,
  library,
  separator = OzListRowSeparator.Short,
  disabled = false,
  wall,
} = defineProps<{
  wall: Wall | WallSnakeCase | WallWithCurrentUserInfo
  darkMode?: boolean | 'auto'
  invertColors?: boolean
  dateAttribute?: string
  /**
   * Set this to false to use normal cursor, tabindex -1 etc
   */
  clickable?: boolean
  navigateOnClick?: boolean
  xMore?: boolean
  /**
   * Whether there is a library associated with the wall
   */
  library?: Library | null
  separator?: OzListRowSeparator
  disabled?: boolean
}>()

const emit = defineEmits<{
  (name: 'select', wall: Wall | WallSnakeCase): void
  (name: 'more', wall: Wall | WallSnakeCase): void
}>()

const title = computed((): string => {
  return wall.title || __('Untitled padlet')
})

const wallLabel = computed((): string => {
  return clickable && navigateOnClick
    ? __('Go to %{wallTitle} padlet', { wallTitle: title.value })
    : __('Select %{wallTitle} padlet', { wallTitle: title.value })
})

const dominantColorAsRgb = computed((): string => {
  return (wall as Wall).thumbnail.dominantColorAsRgb || (wall as WallSnakeCase).thumbnail.dominant_color_as_rgb
})

const date = computed((): string => {
  if (dateAttribute === 'updatedAt') {
    return (wall as Wall).updatedAt || (wall as WallSnakeCase).updated_at
  } else {
    return wall[dateAttribute]
  }
})

const getRowAuthorDisplayName = (wall: Wall | WallSnakeCase | WallWithCurrentUserInfo): string | undefined => {
  const wallWithUserInfo = wall as WallWithCurrentUserInfo
  if (wallWithUserInfo.currentUserCanWrite == null || wallWithUserInfo.currentUserIsBuilder == null) {
    return undefined
  }

  if (!wallWithUserInfo.currentUserCanWrite) {
    return __('No writer permissions')
  }

  return undefined
}

const accountDisplayName = computed((): string => {
  const authorDisplayText = getRowAuthorDisplayName(wall)
  const displayName =
    authorDisplayText ?? ('displayName' in wall.builder ? wall.builder.displayName : wall.builder.display_name)
  return library ? library.name : displayName
})

const daysTillWallIsDeleted = computed((): number => {
  const daysLeft = 'daysTillDeletion' in wall ? wall.daysTillDeletion : (wall as WallSnakeCase).days_till_deletion
  return daysLeft ?? 0
})

const daysTillDeletedDisplay = computed((): string => {
  return n__('%{num} day left', '%{num} days left', daysTillWallIsDeleted.value, { num: daysTillWallIsDeleted.value })
})

const wallDeletingSoon = computed((): boolean => {
  return daysTillWallIsDeleted.value <= 3
})

const xRelativeTimestamp = computed((): boolean => {
  return dateAttribute !== 'trashedAt'
})

const xTemplate = computed((): boolean => {
  if ('isTemplate' in wall) {
    return wall.isTemplate === true
  }
  return false
})

const isFaded = computed(() => {
  const wallWithUserInfo = wall as WallWithCurrentUserInfo

  if (wallWithUserInfo.currentUserCanWrite == null || wallWithUserInfo.currentUserIsBuilder == null) {
    return false
  }
  if (wallWithUserInfo.currentUserCanWrite) {
    return false
  }
  return true
})

const select = (): void => {
  if (disabled) {
    return
  }
  emit('select', wall)
}

const more = (): void => {
  emit('more', wall)
}
</script>

<script lang="ts">
export { OzListRowSeparator } from '@@/library/v4/components/OzListRow.vue'
export default {}
</script>

<template>
  <OzListRow
    :dark-mode="darkMode"
    :separator="separator"
    :clickable="clickable"
    :aria-label="wallLabel"
    @click="select"
  >
    <template #left>
      <ImageThumbnail
        :class="['rounded-lg rounded-ee-2.25xl me-3.5 shrink-0', isFaded && 'opacity-30']"
        :src="wall.thumbnail.url"
        :placeholder-color-value="dominantColorAsRgb"
        :placeholder-color="'random'"
        :fallback-to-placeholder="true"
        :original-image-width="wall.thumbnail.width || 40"
        :original-image-height="wall.thumbnail.height || 40"
        :width="40"
        :process-image="true"
        :aspect-ratio="1"
      />
    </template>

    <div class="my-2 relative overflow-hidden">
      <div
        :class="[
          'text-17-21',
          'font-semibold',
          invertColors
            ? {
                'text-dark-text-100': darkMode === true && !isFaded,
                'text-dark-text-300': darkMode === true && isFaded,
                'text-light-text-100': darkMode === false && !isFaded,
                'text-light-text-300': darkMode === false && isFaded,
                'dark:text-dark-text-100 text-light-text-100': darkMode === 'auto' && !isFaded,
                'text-dark-text-300 dark:text-light-text-300': darkMode === 'auto' && isFaded,
              }
            : {
                'text-light-text-100': darkMode === true && !isFaded,
                'text-light-text-300': darkMode === true && isFaded,
                'text-dark-text-100': darkMode === false && !isFaded,
                'text-dark-text-300': darkMode === false && isFaded,
                'text-dark-text-100 dark:text-light-text-100': darkMode === 'auto' && !isFaded,
                'text-dark-text-300 dark:text-light-text-300': darkMode === 'auto' && isFaded,
              },
          'truncate',
        ]"
      >
        {{ title }}
      </div>
      <div
        :class="[
          'text-body-extra-small',
          'flex',
          'leading-4',
          invertColors
            ? {
                'text-dark-text-100': darkMode === true,
                'text-light-text-100': darkMode === false,
                'dark:text-dark-text-100 text-light-text-100': darkMode === 'auto',
              }
            : {
                'text-light-text-200': darkMode === true,
                'text-dark-text-200': darkMode === false,
                'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
              },
        ]"
      >
        <span class="truncate">{{ accountDisplayName }}</span>
        <span
          :class="[
            invertColors
              ? {
                  'text-dark-text-300': darkMode === true,
                  'text-light-text-300': darkMode === false,
                  'text-light-text-300 dark:text-dark-text-300': darkMode === 'auto',
                }
              : {
                  'text-light-text-300': darkMode === true,
                  'text-dark-text-300': darkMode === false,
                  'text-dark-text-300 dark:text-light-text-300': darkMode === 'auto',
                },
          ]"
          >&nbsp;•&nbsp;</span
        >
        <OzRelativeTime v-if="xRelativeTimestamp" :datetime="date" dateformat="short" />
        <span v-else :class="[wallDeletingSoon && 'text-danger-100']">{{ daysTillDeletedDisplay }}</span>
        <OzBadge
          v-if="xTemplate"
          class="h-4 ms-3 bg-teal-600 text-light-text-100"
          :text="__('Template')"
          :size-preset="OzBadgeSizePreset.Tiny"
          :color-scheme="OzBadgeColorScheme.Bare"
        />
      </div>
    </div>

    <slot name="right" v-if="$slots.right" />

    <template v-if="xMore" #right>
      <OzPlainButton
        :class="['ms-1 -me-1']"
        :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
        :size-preset="OzPlainButtonSizePreset.H28px"
        :data-testid="title"
        :aria-label="__('More')"
        @click.stop="more"
      >
        <OzIcon :size="20" name="more_vertical" />
      </OzPlainButton>
    </template>
  </OzListRow>
</template>
